import React from 'react';
import { TransparentBg, ModalBody, Header, Title, CloseModalButton } from './styled';
import { ReactComponent as XIcon } from 'assets/icons/ui-elements/small/x-mark-sm.svg';
import { useModalBehavior } from './useModalBehavior';

export interface ModalRenderState {
  isEntering: boolean;
  isExiting: boolean;
  contentRef: React.RefObject<HTMLDivElement>;
}

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Render-prop: a própria caixa de conteúdo (ex: `ConfirmDeleteBox`) recebe o estado
   *  da transição e aplica `data-entering`/`data-exiting`/`contentRef` a si mesma —
   *  o `Modal` não impõe nenhuma caixa/estilo próprio, só o overlay com fade. */
  children: (state: ModalRenderState) => React.ReactNode;
  closeOnEsc?: boolean;
  closeOnOverlayClick?: boolean;
  className?: string;
}

export const Modal: React.FC<ModalProps> = ({
  isOpen,
  onClose,
  children,
  closeOnEsc,
  closeOnOverlayClick,
  className,
}) => {
  const { shouldRender, isEntering, isExiting, contentRef, handleOverlayClick } = useModalBehavior({
    isOpen,
    onClose,
    closeOnEsc,
    closeOnOverlayClick,
  });

  if (!shouldRender) return null;

  return (
    <TransparentBg
      className={className}
      data-entering={isEntering}
      data-exiting={isExiting}
      onClick={handleOverlayClick}
      role="presentation"
    >
      {children({ isEntering, isExiting, contentRef })}
    </TransparentBg>
  );
};

export interface FormModalProps {
  setClose: (value: boolean) => void;
  title?: string;
  children?: React.ReactNode;
  isOpen?: boolean;
  className?: string;
}

export const FormModal: React.FC<FormModalProps> = ({ setClose, title, children, isOpen = true, className }) => {
  return (
    <Modal
      isOpen={isOpen}
      onClose={() => setClose(false)}
      className={className}
      closeOnEsc={false}
      closeOnOverlayClick={false}
    >
      {({ isEntering, isExiting, contentRef }) => (
        <ModalBody ref={contentRef} data-entering={isEntering} data-exiting={isExiting} role="dialog" aria-modal="true">
          <Header>
            <Title>{title}</Title>
            <CloseModalButton onClick={() => setClose(false)}>
              <XIcon />
            </CloseModalButton>
          </Header>
          {children}
        </ModalBody>
      )}
    </Modal>
  );
};
